/**
 * TanStack Query hooks for company management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { companiesAPI, type CreateCompanyInput, type UpdateCompanyInput } from '@/lib/api/companies'
import { toast } from 'sonner'

export const companyKeys = {
  all: ['companies'] as const,
  lists: () => [...companyKeys.all, 'list'] as const,
  list: (params?: Record<string, string | number | undefined>) => [...companyKeys.lists(), params] as const,
  details: () => [...companyKeys.all, 'detail'] as const,
  detail: (id: string) => [...companyKeys.details(), id] as const,
  stats: () => [...companyKeys.all, 'stats'] as const,
}

export function useCompanies(params?: { page?: number; perPage?: number; search?: string; status?: string }) {
  return useQuery({
    queryKey: companyKeys.list(params),
    queryFn: () => companiesAPI.list(params),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: companyKeys.detail(id),
    queryFn: () => companiesAPI.get(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  })
}

export function useCompanyStats() {
  return useQuery({
    queryKey: companyKeys.stats(),
    queryFn: () => companiesAPI.stats(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCompanyInput) => companiesAPI.create(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyKeys.lists() })
      void qc.invalidateQueries({ queryKey: companyKeys.stats() })
      toast.success('Company created')
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to create company'),
  })
}

export function useUpdateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyInput }) => companiesAPI.update(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyKeys.all })
      toast.success('Company updated')
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to update company'),
  })
}

export function useDeactivateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => companiesAPI.deactivate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyKeys.all })
      toast.success('Company deactivated')
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to deactivate company'),
  })
}
